import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  createYear as createYearService,
  updateYear as updateYearService,
  deleteYear as deleteYearService, 
  getYears as getYearsService
} from "../../services/tenant/year.service";
import { validate } from "../../utils";
import { handlerErrorV2 } from "../../utils/errors";
import { updateYearsList, updateYearsListMethod } from "../../store/actions/tenant.actions";
import isEmpty from 'lodash/isEmpty';

const useYears = () => {
  const { enqueueSnackbar } = useSnackbar()

  const dispatch = useDispatch();
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [errors, setErrors] = useState({});
  
  const getYears = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const response = await getYearsService();
      dispatch(updateYearsList(response?.data || []));
    } catch (error) {
      setErrors({ message: error.message || 'An error ocurred'});
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  useEffect(() => {
    getYears();
  }, []);

  const validateForm = (form) => {
    const fieldRules = {
      name: 'required',
      shortName: 'required',
      position: 'required|numeric'
    }

    const errors = validate(fieldRules, form)
    if (!isEmpty(errors)) {
      return { error: true, ...errors }
    }

    return { error: false }
  }

  const createYear = async (form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result
    };

    try {
      const response = await createYearService(form);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateYearsListMethod('POST', response.data));
        enqueueSnackbar('Year created successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const updateYear = async (yearId, form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result
    };

    try {
      const response = await updateYearService(yearId, form);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateYearsListMethod('PUT', response.data));
        enqueueSnackbar('Year updated successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const deleteYear = async (yearId) => {
    try {
      const response = await deleteYearService(yearId);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateYearsListMethod('DELETE', yearId));
        enqueueSnackbar('Year deleted successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  return {
    errors,
    isLoadingList,
    getYears,
    createYear,
    updateYear,
    deleteYear
  }
};

export default useYears;